import { ReportType } from '@/common/constants/report';
import {
    CommentContent,
    CommentMentionContent,
    DailyScrumCommentContent,
    FeedbackCommentContent,
    FeedbackReactionContent,
    FeedbackReceivedContent,
    FeedbackRequestContent,
    FeedbackRequestRejectedContent,
    GoalDeadlineChangedContent,
    GoalLeaderChangedContent,
    GoalPriorityChangedContent,
    GoalStateChangedContent,
    MentionContent,
    NotificationType,
    PlanRequestContent,
    ProjectDeadlineChangedContent,
    ProjectLeaderChangedContent,
    ProjectPriorityChangedContent,
    ProjectStateChangedContent,
    ReplyContent,
    ReportContent,
    TeamInviteContent,
    TeamOutContent,
    TodoAssignedContent,
    TodoDeadlineChangedContent,
    TodoStateChangedContent,
    TodoTimeChangedContent,
    WorkspaceInviteContent,
    WorkspaceJoinContent,
    WorkspaceOutContent,
} from '../constants/notification';
import { useCreateNotification } from './useNotificationQueries';
import { limitNotificationContentLength } from '../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

// 워크스페이스

/**
 * 워크스페이스 초대 알림 생성
 */
export function useWorkspaceInviteNotification() {
    const { t } = useTranslation('workspace');
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string; // 알림을 받을 사람
        workspaceId: string; // 워크스페이스 ID
        workspaceInviteContent: WorkspaceInviteContent;
    }) => {
        const { userId, workspaceId, workspaceInviteContent } = params;

        const content: WorkspaceInviteContent = {
            ...workspaceInviteContent,
            workspace_name:
                workspaceInviteContent.workspace_name ||
                t('setting.general.workspace_name'),
            workspace_image_url:
                workspaceInviteContent.workspace_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.WORKSPACE_INVITE,
            content,
            relatedId: workspaceId,
        });
    };

    return {
        createWorkspaceInviteNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 워크스페이스 초대 수락 알림 생성
 */
export function useWorkspaceJoinNotification() {
    const { t } = useTranslation('workspace');
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string; // 알림을 받을 사람
        workspaceId: string; // 워크스페이스 ID
        workspaceJoinContent: WorkspaceJoinContent;
    }) => {
        const { userId, workspaceId, workspaceJoinContent } = params;

        const content: WorkspaceJoinContent = {
            ...workspaceJoinContent,
            workspace_name:
                workspaceJoinContent.workspace_name ||
                t('setting.general.workspace_name'),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.WORKSPACE_JOIN,
            content,
            relatedId: workspaceId,
        });
    };

    return {
        createWorkspaceJoinNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 워크스페이스 방출 알림 생성
 */
export function useWorkspaceOutNotification() {
    const { t } = useTranslation('workspace');
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        workspaceOutContent: WorkspaceOutContent;
    }) => {
        const { userId, workspaceOutContent } = params;

        const content: WorkspaceOutContent = {
            ...workspaceOutContent,
            workspace_name:
                workspaceOutContent.workspace_name ||
                t('setting.general.workspace_name'),
            workspace_image_url: workspaceOutContent.workspace_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.WORKSPACE_OUT,
            content,
            relatedId: workspaceOutContent.workspace_id,
        });
    };

    return {
        createWorkspaceOutNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

// =============
// 팀

/**
 * 팀 초대 알림 생성
 */
export function useTeamInviteNotification() {
    const { t } = useTranslation(['team', 'workspace']);
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        teamId: string;
        teamInviteContent: TeamInviteContent;
    }) => {
        const { userId, teamId, teamInviteContent } = params;

        const content: TeamInviteContent = {
            ...teamInviteContent,
            team_name: teamInviteContent.team_name || t('team:title'),
            team_image_url: teamInviteContent.team_image_url || '',
            workspace_name:
                teamInviteContent.workspace_name ||
                t('workspace:setting.general.workspace_name'),
            inviter_name:
                teamInviteContent.inviter_name ||
                t('team:member.placeholders.inviter'),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.TEAM_INVITE,
            content,
            relatedId: teamId,
        });
    };

    return {
        createTeamInviteNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 팀 방출 알림 생성
 */
export function useTeamOutNotification() {
    const { t } = useTranslation(['team', 'workspace']);
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        teamOutContent: TeamOutContent;
    }) => {
        const { userId, teamOutContent } = params;

        const content: TeamOutContent = {
            ...teamOutContent,
            team_name: teamOutContent.team_name || t('team:title'),
            team_image_url: teamOutContent.team_image_url || '',
            workspace_name:
                teamOutContent.workspace_name ||
                t('workspace:setting.general.workspace_name'),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.TEAM_OUT,
            content,
            relatedId: teamOutContent.team_id,
        });
    };

    return {
        createTeamOutNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

// =============
// 목표
/**
 * 목표 우선순위 변경 알림 생성
 */
export function useGoalPriorityChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        goalId: string;
        goalPriorityChangedContent: GoalPriorityChangedContent;
    }) => {
        const { userId, goalId, goalPriorityChangedContent } = params;

        const content: GoalPriorityChangedContent = {
            ...goalPriorityChangedContent,
            changer_image_url:
                goalPriorityChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.GOAL_PRIORITY_CHANGED,
            content,
            relatedId: goalId,
        });
    };

    return {
        createGoalPriorityChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 목표 상태 변경 알림 생성
 */
export function useGoalStateChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        goalId: string;
        goalStateChangedContent: GoalStateChangedContent;
    }) => {
        const { userId, goalId, goalStateChangedContent } = params;

        const content: GoalStateChangedContent = {
            ...goalStateChangedContent,
            changer_image_url: goalStateChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.GOAL_STATE_CHANGED,
            content,
            relatedId: goalId,
        });
    };

    return {
        createGoalStateChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 목표 마감기한 변경 알림 생성
 */
export function useGoalDeadlineChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        goalId: string;
        goalDeadlineChangedContent: GoalDeadlineChangedContent;
    }) => {
        const { userId, goalId, goalDeadlineChangedContent } = params;

        const content: GoalDeadlineChangedContent = {
            ...goalDeadlineChangedContent,
            changer_image_url:
                goalDeadlineChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.GOAL_DEADLINE_CHANGED,
            content,
            relatedId: goalId,
        });
    };

    return {
        createGoalDeadlineChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 목표 관리자 변경 알림 생성
 */
export function useGoalLeaderChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        goalId: string;
        goalLeaderChangedContent: GoalLeaderChangedContent;
    }) => {
        const { userId, goalId, goalLeaderChangedContent } = params;

        const content: GoalLeaderChangedContent = {
            ...goalLeaderChangedContent,
            changer_image_url: goalLeaderChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.GOAL_LEADER_CHANGED,
            content,
            relatedId: goalId,
        });
    };

    return {
        createGoalLeaderChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

// =============
// 할 일

/**
 * 할 일 할당 알림 생성
 */
export function useTodoAssignedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        todoId: string;
        todoAssignedContent: TodoAssignedContent;
    }) => {
        const { userId, todoId, todoAssignedContent } = params;

        const content: TodoAssignedContent = {
            ...todoAssignedContent,
            assigner_image_url: todoAssignedContent.assigner_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.TODO_ASSIGNED,
            content,
            relatedId: todoId,
        });
    };

    return {
        createTodoAssignedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 할 일 상태 변경 알림 생성
 */
export function useTodoStateChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        todoId: string;
        todoStateChangedContent: TodoStateChangedContent;
    }) => {
        const { userId, todoId, todoStateChangedContent } = params;

        const content: TodoStateChangedContent = {
            ...todoStateChangedContent,
            changer_image_url: todoStateChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.TODO_STATE_CHANGED,
            content,
            relatedId: todoId,
        });
    };

    return {
        createTodoStateChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 할 일 마감기한 변경 알림 생성
 */
export function useTodoDeadlineChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        todoId: string;
        todoDeadlineChangedContent: TodoDeadlineChangedContent;
    }) => {
        const { userId, todoId, todoDeadlineChangedContent } = params;

        const content: TodoDeadlineChangedContent = {
            ...todoDeadlineChangedContent,
            changer_image_url:
                todoDeadlineChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.TODO_DEADLINE_CHANGED,
            content,
            relatedId: todoId,
        });
    };

    return {
        createTodoDeadlineChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 할 일 시간 변경 알림 생성
 */
export function useTodoTimeChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        todoId: string;
        todoTimeChangedContent: TodoTimeChangedContent;
    }) => {
        const { userId, todoId, todoTimeChangedContent } = params;

        const content: TodoTimeChangedContent = {
            ...todoTimeChangedContent,
            changer_image_url: todoTimeChangedContent.changer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.TODO_TIME_CHANGED,
            content,
            relatedId: todoId,
        });
    };

    return {
        createTodoTimeChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

// =============
// 프로젝트

/**
 * 프로젝트 우선순위 변경 알림 생성
 */
export function useProjectPriorityChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        projectId: string;
        projectPriorityChangedContent: ProjectPriorityChangedContent;
    }) => {
        const { userId, projectId, projectPriorityChangedContent } = params;

        const content: ProjectPriorityChangedContent = {
            ...projectPriorityChangedContent,
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.PROJECT_PRIORITY_CHANGED,
            content,
            relatedId: projectId,
        });
    };

    return {
        createProjectPriorityChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 프로젝트 상태 변경 알림 생성
 */
export function useProjectStateChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        projectId: string;
        projectStateChangedContent: ProjectStateChangedContent;
    }) => {
        const { userId, projectId, projectStateChangedContent } = params;

        const content: ProjectStateChangedContent = {
            ...projectStateChangedContent,
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.PROJECT_STATE_CHANGED,
            content,
            relatedId: projectId,
        });
    };

    return {
        createProjectStateChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 프로젝트 마감기한 변경 알림 생성
 */
export function useProjectDeadlineChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        projectId: string;
        projectDeadlineChangedContent: ProjectDeadlineChangedContent;
    }) => {
        const { userId, projectId, projectDeadlineChangedContent } = params;

        const content: ProjectDeadlineChangedContent = {
            ...projectDeadlineChangedContent,
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.PROJECT_DEADLINE_CHANGED,
            content,
            relatedId: projectId,
        });
    };

    return {
        createProjectDeadlineChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 프로젝트 책임자 변경 알림 생성
 */
export function useProjectLeaderChangedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        projectId: string;
        projectLeaderChangedContent: ProjectLeaderChangedContent;
    }) => {
        const { userId, projectId, projectLeaderChangedContent } = params;

        const content: ProjectLeaderChangedContent = {
            ...projectLeaderChangedContent,
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.PROJECT_LEADER_CHANGED,
            content,
            relatedId: projectId,
        });
    };

    return {
        createProjectLeaderChangedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

// =============
// 댓글

/**
 * 댓글 알림 생성
 */
export function useCommentNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        commentId: string;
        commentContent: CommentContent;
    }) => {
        const { userId, commentId, commentContent } = params;

        const content: CommentContent = {
            ...commentContent,
            comment_text: limitNotificationContentLength(
                commentContent.comment_text
            ),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.COMMENT,
            content,
            relatedId: commentId,
        });
    };

    return {
        createCommentNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 답글 알림 생성
 */
export function useReplyNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        replyId: string;
        replyContent: ReplyContent;
    }) => {
        const { userId, replyId, replyContent } = params;

        const content: ReplyContent = {
            ...replyContent,
            reply_text: limitNotificationContentLength(replyContent.reply_text),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.REPLY,
            content,
            relatedId: replyId,
        });
    };

    return {
        createReplyNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 댓글 멘션 관련 알림 생성
 */
export function useCommentMentionNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        // mentionId: string;
        commentMentionContent: CommentMentionContent;
    }) => {
        const { userId, commentMentionContent } = params;

        const content: CommentMentionContent = {
            ...commentMentionContent,
            comment_text: limitNotificationContentLength(
                commentMentionContent.comment_text
            ),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.COMMENT_MENTION,
            content,
            relatedId: commentMentionContent.comment_id,
        });
    };

    return {
        createCommentMentionNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 노트 멘션 관련 알림 생성
 */
export function useMentionNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        mentionContent: MentionContent;
    }) => {
        const { userId, mentionContent } = params;

        const content: MentionContent = {
            ...mentionContent,
            mentioner_name: mentionContent.mentioner_name || '',
            mentioner_image_url: mentionContent.mentioner_image_url || '',
            mentioner_id: mentionContent.mentioner_id || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.MENTION,
            content,
            relatedId: mentionContent.context_id,
        });
    };

    return {
        createMentionNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 피드백 댓글 및 답글 알림 생성
 * @param userId 알림 받을 사용자 uid
 * @param params 피드백 댓글 및 답글 알림 콘텐츠
 */
export function useFeedbackCommentNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async ({
        userId,
        params,
    }: {
        userId: string;
        params: FeedbackCommentContent;
    }) => {
        const { comment_id: commentId } = params;

        const content: FeedbackCommentContent = {
            ...params,
            comment_text: limitNotificationContentLength(params.comment_text),
            feedback_content: limitNotificationContentLength(
                params.feedback_content
            ),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.FEEDBACK_COMMENT,
            content,
            relatedId: commentId,
        });
    };

    return {
        createFeedbackCommentNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 데일리스크럼 댓글 및 답글 알림 생성
 * @param userId 알림 받을 사용자 uid
 * @param params 데일리스크럼 댓글 및 답글 알림 콘텐츠
 */
export function useDailyScrumCommentNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async ({
        userId,
        params,
    }: {
        userId: string;
        params: DailyScrumCommentContent;
    }) => {
        const { comment_id: commentId } = params;

        const content: DailyScrumCommentContent = {
            ...params,
            comment_text: limitNotificationContentLength(params.comment_text),
            // daily_scrum_content: limitNotificationContentLength(
            //     params.daily_scrum_content
            // ),
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.DAILY_SCRUM_COMMENT,
            content,
            relatedId: commentId,
        });
    };

    return {
        createDailyScrumCommentNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 보고서 수신 및 업데이트 알림 생성
 */
export function useReportNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        reportId: string;
        reportContent: ReportContent;
        notificationType:
            | NotificationType.REPORT_RECEIVE
            | NotificationType.REPORT_UPDATED;
    }) => {
        const { userId, reportId, reportContent, notificationType } = params;

        const content = {
            ...reportContent,
            workspace_name: reportContent.workspace_name || '',
            workspace_image_url: reportContent.workspace_image_url || '',
            writer_name: reportContent.writer_name || '',
            writer_image_url: reportContent.writer_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: notificationType,
            content,
            relatedId: reportId,
        });
    };

    return {
        createReportNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 피드백 수신 및 업데이트 알림 생성
 * @param userId 알림을 받을 사용자 uid
 * @param params 피드백 수신 알림 콘텐츠
 * 업데이트일 경우 params의 is_updated를 true로 설정
 */
export function useFeedbackReceiveNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async ({
        userId,
        params,
    }: {
        userId: string;
        params: FeedbackReceivedContent;
    }) => {
        const { feedback_id: feedbackId } = params;

        // feedback_content의 길이를 제한해 필요 이상으로 긴 텍스트 저장 방지
        const content: FeedbackReceivedContent = {
            ...params,
            feedback_content: limitNotificationContentLength(
                params.feedback_content
            ),
        };

        return sendNotification.mutateAsync({
            userId,
            type: params.is_updated
                ? NotificationType.FEEDBACK_UPDATED
                : NotificationType.FEEDBACK_RECEIVED,
            content,
            relatedId: feedbackId,
        });
    };

    return {
        createFeedbackReceiveNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 피드백 요청 수신 및 업데이트 알림 생성
 * @param userId 알림을 받을 사용자 uid
 * @param params 피드백 요청 알림 콘텐츠
 * 업데이트일 경우 params의 is_updated를 true로 설정
 */
export function useFeedbackRequestNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async ({
        userId,
        params,
    }: {
        userId: string;
        params: FeedbackRequestContent;
    }) => {
        const { request_id: requestId } = params;

        // request_content의 길이를 제한해 필요 이상으로 긴 텍스트 저장 방지
        const content: FeedbackRequestContent = {
            ...params,
            request_content: limitNotificationContentLength(
                params.request_content
            ),
        };

        return sendNotification.mutateAsync({
            userId,
            type: params.is_updated
                ? NotificationType.FEEDBACK_REQUEST_UPDATED
                : NotificationType.FEEDBACK_REQUEST,
            content,
            relatedId: requestId,
        });
    };

    return {
        createFeedbackRequestNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 피드백 요청 거절 알림 생성
 * @param userId 알림을 받을 사용자 uid
 * @param params 피드백 요청 거절 알림 콘텐츠
 * request_id - 거절할 피드백 요청 ID
 */
export function useFeedbackRequestRejectedNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async ({
        userId,
        params,
    }: {
        userId: string;
        params: FeedbackRequestRejectedContent;
    }) => {
        const { request_id: requestId } = params;

        const content: FeedbackRequestRejectedContent = {
            ...params,
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.FEEDBACK_REQUEST_REJECTED,
            content,
            relatedId: requestId,
        });
    };

    return {
        createFeedbackRequestRejectedNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 피드백 이모지 리액션 알림 생성
 */
export function useFeedbackReactionNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        notiContent: FeedbackReactionContent;
    }) => {
        const { feedback_id: feedbackId } = params.notiContent;

        const content: FeedbackReactionContent = {
            ...params.notiContent,
            feedback_content: limitNotificationContentLength(
                params.notiContent.feedback_content
            ),
        };

        return sendNotification.mutateAsync({
            userId: params.userId,
            type: NotificationType.FEEDBACK_REACTION,
            content,
            relatedId: feedbackId, // 리액션은 여러개여서 피드백 ID로 연결, relatedType도 'feedback'
        });
    };

    return {
        createFeedbackReactionNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}

/**
 * 계획 수립 요청 알림 생성
 */
export function usePlanRequestNotification() {
    const sendNotification = useCreateNotification();

    const createNotification = async (params: {
        userId: string;
        planRequestContent: PlanRequestContent;
    }) => {
        const { userId, planRequestContent } = params;

        const content: PlanRequestContent = {
            ...planRequestContent,
            requester_name: planRequestContent.requester_name || '',
            requester_image_url: planRequestContent.requester_image_url || '',
        };

        return sendNotification.mutateAsync({
            userId,
            type: NotificationType.PLAN_REQUEST,
            content,
            relatedId: userId,
        });
    };

    return {
        createPlanRequestNotification: createNotification,
        isPending: sendNotification.isPending,
        isError: sendNotification.isError,
        error: sendNotification.error,
    };
}
